import styled from '@emotion/styled'
import { Stack } from 'components/Stack'
import { Text } from 'components/Typography'
import { Icon } from 'components/icon'
import { useTranslation } from 'next-i18next'
import { FC } from 'react'
import { colors } from 'utils/colors'

type PasswordErrorProps = { allErrors?: Record<string, string> }

export const PasswordError: FC<PasswordErrorProps> = ({ allErrors }) => {
  const { t } = useTranslation('common')
  const allValidations = [
    'at_least_8_chars',
    'at_most_64_chars',
    'at_least_one_lowercase',
    'at_least_one_uppercase',
    'at_least_one_number',
    'password_repeated_chars',
    'sequential_chars',
  ] as const

  const errorsWeHave = allErrors != null ? Object.keys(allErrors) : []

  const errors = allValidations.map((validation) => ({
    message: validation,
    valid: !errorsWeHave.includes(validation),
  }))

  const allAreValid = errors.every((error) => error.valid)
  if (allAreValid) {
    return null
  }

  return (
    <SPopover>
      <SList>
        {errors.map((error) => (
          <SingleError
            valid={error.valid}
            message={t(`common.form.${error.message}`)}
            key={error.message}
          />
        ))}
      </SList>
    </SPopover>
  )
}

type SingleErrorProps = { valid: boolean; message: string }

const SingleError: FC<SingleErrorProps> = ({ valid, message }) => {
  const icon = valid ? 'Check' : ('Warning' as const)
  const iconColor = valid ? colors.green[200] : colors.red[500]

  return (
    <SListItem>
      <Stack space="0.5rem" orientation="horizontal" css={{ alignItems: 'center' }}>
        <Icon name={icon} color={iconColor} size="0.75rem" />
        <Text color="black" variant="bodySmall">
          {message}
        </Text>
      </Stack>
    </SListItem>
  )
}

const SList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`

const SListItem = styled.li``

const SPopover = styled.div`
  border: 2px solid #ffffff;
  position: absolute;
  width: 100%;
  background: var(--c-white);
  box-shadow: 0px 5px 40px 0px #d2dce1;
  padding: 1rem 0.5rem 1rem 1rem;
  z-index: 1;
  margin-top: 0.5rem;
`
